import React, { useMemo } from 'react';
import { useCustomDataColumnsQuery } from 'api/useCustomDataColumnsQuery';
import { usePanelPropertiesGroupsQuery } from 'api/usePanelPropertiesGroupsQuery';

import { ManageColumnsModal } from './ManageColumnsModal';
import { buildAllColumnSections, resolveSavedState } from './buildColumns';

/**
 * Connects ManageColumnsModal to data sources and translates the form's
 * flat "checked column ids" output back into the two storage shapes
 * StudyPage persists.
 *
 * The class parent (StudyPage) passes down its authoritative state
 * (`study`, `tableColumnsHidden`, `panelPropertyIds`) and receives
 * a single `onSave({ tableColumnsHidden, panelPropertyIds })` callback
 * when the user saves the modal. Persistence (localStorage writes)
 * happens in the parent so all of the column state lives in one place.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {Object} props.study
 * @param {string[]} props.tableColumnsHidden
 * @param {number[]} props.panelPropertyIds
 * @param {(state: { tableColumnsHidden: string[], panelPropertyIds: number[] }) => void} props.onSave
 */
export function ManageColumnsModalConnected({ isOpen, onClose, study, tableColumnsHidden, panelPropertyIds, onSave }) {
    const { data: panelColumns = [], isLoading: isColumnsLoading } = useCustomDataColumnsQuery({
        enabled: isOpen
    });
    const { data: panelPropertiesGroups = [], isLoading: isGroupsLoading } = usePanelPropertiesGroupsQuery({
        enabled: isOpen
    });

    // Compute sections only when the modal actually opens so closed
    // modals never pay for the rebuild.
    const sections = useMemo(
        () =>
            isOpen
                ? buildAllColumnSections({
                      study,
                      panelColumns,
                      panelPropertiesGroups,
                      tableColumnsHidden,
                      panelPropertyIds
                  })
                : [],
        [isOpen, study, panelColumns, panelPropertiesGroups, tableColumnsHidden, panelPropertyIds]
    );

    const handleSave = checkedIds => {
        const next = resolveSavedState({
            sections,
            checkedIds,
            previousHidden: tableColumnsHidden
        });
        onSave(next);
    };

    return (
        <ManageColumnsModal
            isOpen={isOpen}
            onClose={onClose}
            sections={sections}
            isLoading={isColumnsLoading || isGroupsLoading}
            onSave={handleSave}
        />
    );
}
